/**
 * MatchProvenanceChip — a compact "why did this match?" affordance.
 *
 * Surfaces the evidence the AI used to make a non-obvious match, inline next to
 * the matched value. Two variants:
 *  - `memory-note`: a learned fact a teammate taught the system earlier
 *    (e.g. a parent-company → vendor mapping).
 *  - `web-search`: a web-agent lookup that resolved a synonym / equivalence
 *    (e.g. "yams" == "sweet potatoes").
 *
 * Purely presentational: render it only when there's provenance to show
 * (callers pass `undefined` to render nothing), so it is inert without data.
 */

import {
  BrainIcon,
  GlobeIcon,
  ArrowSquareOutIcon,
  CheckCircleIcon,
} from '@phosphor-icons/react';
import { T, Var } from 'gt-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export interface MatchProvenance {
  kind: 'memory-note' | 'web-search';
  /** One-line heading shown on the popover. */
  title: string;
  /** The learned note / equivalence statement. */
  detail: string;
  // memory-note
  actor?: string;
  when?: string;
  // web-search
  query?: string;
  resultSnippet?: string;
  sourceUrl?: string;
  sourceLabel?: string;
}

export function MatchProvenanceChip({
  provenance,
  className,
  onSaveToMemory,
  savedToMemory,
}: {
  provenance?: MatchProvenance | null;
  className?: string;
  /**
   * When provided (web-search variant), renders a "Save to memory" CTA so the
   * user can teach the system this equivalence — next time it's a memory match
   * instead of a fresh web lookup.
   */
  onSaveToMemory?: () => void;
  /** When true, the equivalence has already been saved to the memory bank. */
  savedToMemory?: boolean;
}) {
  if (!provenance) return null;

  const isMemory = provenance.kind === 'memory-note';
  const Icon = isMemory ? BrainIcon : GlobeIcon;
  const chipClasses = isMemory
    ? 'border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100'
    : 'border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={className}
        >
          <Badge
            variant="outline"
            className={`gap-1 cursor-pointer text-[10px] font-medium ${chipClasses}`}
          >
            <Icon className="size-3 shrink-0" weight="fill" />
            {isMemory ? (
              <T context="match provenance chip: learned memory note">
                Matched via memory note
              </T>
            ) : (
              <T context="match provenance chip: web agent lookup">
                Matched via web search
              </T>
            )}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 space-y-2 p-3 text-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-1.5 font-medium">
          <Icon
            className={`size-4 shrink-0 ${isMemory ? 'text-violet-600' : 'text-sky-600'}`}
            weight="fill"
          />
          {provenance.title}
        </div>
        <p className="text-muted-foreground">{provenance.detail}</p>

        {provenance.kind === 'web-search' && provenance.query && (
          <div className="rounded-md border bg-muted/40 p-2 text-xs space-y-1">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <GlobeIcon className="size-3" />
              <span className="italic">{`“${provenance.query}”`}</span>
            </div>
            {provenance.resultSnippet && (
              <p className="text-foreground">{provenance.resultSnippet}</p>
            )}
            {provenance.sourceUrl && (
              <a
                href={provenance.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sky-700 hover:underline"
              >
                {provenance.sourceLabel ?? provenance.sourceUrl}
                <ArrowSquareOutIcon className="size-3" />
              </a>
            )}
          </div>
        )}

        {provenance.kind === 'web-search' &&
          (savedToMemory ? (
            <div className="flex items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 px-2 py-1.5 text-xs font-medium text-violet-700">
              <CheckCircleIcon className="size-4 shrink-0" weight="fill" />
              <T context="confirmation that an AI match was saved to the memory bank">
                Saved to memory — we'll reuse this next time
              </T>
            </div>
          ) : onSaveToMemory ? (
            <Button
              size="sm"
              variant="outline"
              className="w-full gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-50"
              onClick={(e) => {
                e.stopPropagation();
                onSaveToMemory();
              }}
            >
              <BrainIcon className="size-3.5" weight="fill" />
              <T context="CTA to save an AI-resolved match to the memory bank">
                Save to memory
              </T>
            </Button>
          ) : null)}

        {provenance.kind === 'memory-note' &&
          (provenance.actor || provenance.when) && (
            <div className="text-xs text-muted-foreground border-t pt-2">
              {provenance.when ? (
                <T context="who taught the system this fact and when">
                  Taught by <Var>{provenance.actor ?? 'a teammate'}</Var> on{' '}
                  <Var>{provenance.when}</Var>
                </T>
              ) : (
                <T context="who taught the system this fact">
                  Taught by <Var>{provenance.actor ?? 'a teammate'}</Var>
                </T>
              )}
            </div>
          )}
      </PopoverContent>
    </Popover>
  );
}
